// ─────────────────────────────────────────────────────────────────────────────
// FTUE tip registry
// ─────────────────────────────────────────────────────────────────────────────
// The catalogue of every tip the product may show. This file is the only thing
// that changes when a tip is added, retired or re-fired — no presenter, no
// renderer, no component code.
//
// Copy lives in i18n under `ftue.tips.<id>.*`; `ftue-tip.registry.spec.ts` fails
// the build when a key is missing in any locale, when an id is duplicated, when
// an anchor is not declared in `ftue-anchors.ts`, or when English copy runs past
// the limits below.
//
// ── The copy contract ────────────────────────────────────────────────────────
// Four slots. Only three of them are yours to write.
//
// 1. `label` — the chip. NOT free text: use the tone's standard label, so five
//    tips never invent five vocabularies for the same three intents.
//       alert → "Needs a decision"   info → "Tip"   new → "New"
//
// 2. `title` — the takeaway, ≤ 40 characters. Sentence case, no full stop.
//    Name the thing on screen ("No company on this order"), or say what to do
//    with it ("Personalise this table"). Never a feature name on its own, never
//    a question, never "Did you know".
//
// 3. `body` — TWO short sentences at most, ≤ 140 characters all in: what
//    happened, then what to do about it. Second person for the user, "Volta" in
//    the third person ("Volta didn't recognise the sender. Pick one now and the
//    next email from that address matches itself."). No exclamation marks, no
//    "simply", no "just". A third sentence means the tip is teaching two things
//    — split it or cut one.
//
// 4. the grey line under the body — framework-owned (`ftue.card.once_note` /
//    `preview_note`). Do NOT write a per-tip variant: it is the promise the
//    presenter actually keeps, and it must read identically everywhere.
//
// `trigger` is a fifth string, and it is not card copy: it is the plain-language
// firing condition shown in Settings → Product tips. Write it as a condition,
// not an instruction ("An order opens with no company matched").
//
// ── Announcements ────────────────────────────────────────────────────────────
// A `new` tip is a release note, not a permanent lesson, so it MUST declare
// `expiresOn` — the registry test enforces it. Bumping `version` re-fires a tip
// for everyone who saw the old one; use it when the point of the tip changed,
// not for a wording fix.
// ─────────────────────────────────────────────────────────────────────────────

import { FTUE_ANCHORS } from './ftue-anchors';
import { IFtueTip } from './ftue.models';

/**
 * Tip ids, so trigger sites name a tip through the registry instead of through
 * a string literal that no rename would ever catch.
 */
export const FTUE_TIP_IDS = {
  ORDER_MISSING_COMPANY: 'order-missing-company',
  ORDER_LINE_ALTERNATIVES: 'order-line-alternatives',
  ORDER_LINE_MANUAL_MATCH: 'order-line-manual-match',
  ORDER_LINE_UNMATCHED: 'order-line-unmatched',
  ORDERS_COLUMN_PERSONALIZATION: 'orders-column-personalization',
} as const;

export type FtueTipId = (typeof FTUE_TIP_IDS)[keyof typeof FTUE_TIP_IDS];

export const FTUE_TIPS: readonly IFtueTip[] = [
  /**
   * An order opened with no company matched: the user has to pick one before
   * anything else is worth doing, hence the highest priority and the `alert`
   * tone. Trigger: `isCompanyInformationMissing()` on the order detail.
   */
  {
    id: FTUE_TIP_IDS.ORDER_MISSING_COMPANY,
    version: 1,
    tone: 'alert',
    priority: 30,
    anchor: FTUE_ANCHORS.ORDER_COMPANY,
    placement: 'bottom',
  },

  /**
   * Volta matched a line through an alternative rather than the exact code, so
   * the result is a suggestion rather than a certainty. Ranks above the
   * unmatched tip below, and is its prerequisite: "these are candidates,
   * compare them" is the lesson that makes "this one matched nothing" legible.
   */
  {
    id: FTUE_TIP_IDS.ORDER_LINE_ALTERNATIVES,
    version: 1,
    tone: 'info',
    priority: 25,
    anchor: FTUE_ANCHORS.ORDER_LINE_ALTERNATIVES,
    placement: 'bottom',
  },

  /**
   * The first line matched by hand. Fires when the "always match" bar appears —
   * i.e. before the user confirms — because the whole point is to get them to
   * flip that toggle, not to tell them afterwards what they missed.
   */
  {
    id: FTUE_TIP_IDS.ORDER_LINE_MANUAL_MATCH,
    version: 1,
    tone: 'info',
    priority: 20,
    anchor: FTUE_ANCHORS.ORDER_LINE_ALWAYS_MATCH,
    placement: 'bottom',
  },

  /**
   * A line Volta could not match at all. Gated behind the alternatives tip
   * (`requires`) so the two never compete for the same order: an order with
   * both conditions teaches alternatives first, and this one waits for a later
   * visit. Lowest priority of the order-detail tips for the same reason.
   */
  {
    id: FTUE_TIP_IDS.ORDER_LINE_UNMATCHED,
    version: 1,
    tone: 'alert',
    priority: 15,
    anchor: FTUE_ANCHORS.ORDER_LINE_UNMATCHED,
    requires: FTUE_TIP_IDS.ORDER_LINE_ALTERNATIVES,
    placement: 'bottom',
  },

  /**
   * Feature announcement: table views are personalisable. Anchored on the
   * orders list only — it is the landing page — with copy that names the icon
   * so the lesson carries to the other tables that show the same button.
   */
  {
    id: FTUE_TIP_IDS.ORDERS_COLUMN_PERSONALIZATION,
    version: 1,
    tone: 'new',
    priority: 10,
    anchor: FTUE_ANCHORS.ORDERS_COLUMN_SETTINGS,
    placement: 'bottom',
    // Roughly two quarters after the release. Past that it is just a feature,
    // and announcing it to someone who joined later would be a lie.
    expiresOn: '2027-03-01',
  },
];

/** Root i18n namespace of the framework. */
export const FTUE_I18N_ROOT = 'ftue';

/**
 * Copy limits, enforced against English by the registry spec. Translations are
 * allowed to run longer — the card grows — but the source language sets the bar,
 * because a title nobody can scan is a title nobody reads in any language.
 */
export const FTUE_COPY_LIMITS = {
  title: 40,
  body: 140,
} as const;

/** Registry lookup. `undefined` for an unknown id — callers decide what that means. */
export function findFtueTip(id: string): IFtueTip | undefined {
  return FTUE_TIPS.find((tip) => tip.id === id);
}

/**
 * The four translation keys every tip must define. `trigger` is only rendered
 * in the Settings inventory, but it is required: a tip nobody can explain to a
 * user has no business interrupting them.
 */
export function ftueTipI18nKeys(tipId: string): {
  label: string;
  title: string;
  body: string;
  trigger: string;
} {
  const prefix = `${FTUE_I18N_ROOT}.tips.${tipId}`;
  return {
    label: `${prefix}.label`,
    title: `${prefix}.title`,
    body: `${prefix}.body`,
    trigger: `${prefix}.trigger`,
  };
}
